import React, { useState, useEffect } from 'react';
import { Modal, Callout, toast } from '@getpingback/realeza';
import { TextField, Button } from '@material-ui/core';
import ContentEditor from './ContentEditor';
import { formatDuration } from '../../../helpers/utils';
import { createLesson, getLessonById, updateLesson } from '../../../services/education';
import { PlusIcon, TrashCanIcon, TimesIcon } from '@stash-ui/regular-icons';

const initialLesson = {
  name: '',
  type: 'course_lesson',
  bannerUrl: '',
  content: '',
  videoUrl: '',
  duration: '',
  supportingMaterials: [],
};

const NewLessonModal = ({ isOpen, onClose, lessonId, courseId, moduleId, onSave }) => {
  const [lesson, setLesson] = useState({ courseId, moduleId, ...initialLesson });

  const formatedMaterial = lesson.supportingMaterials.map((material, index) => ({
    id: index,
    name: material.name,
    url: material.url,
  }));

  useEffect(() => {
    if (lessonId) {
      getLessonDetails();
    }
  }, [lessonId]);

  const handleClose = () => {
    setLesson({ courseId, moduleId, ...initialLesson });
    onClose();
  };

  const getLessonDetails = async () => {
    try {
      const { data } = await getLessonById(lessonId);
      setLesson({
        courseId: courseId,
        type: data.type,
        moduleId: moduleId,
        name: data.name,
        bannerUrl: data.bannerUrl,
        content: data.content,
        videoUrl: data.videoUrl,
        duration: data.duration,
        supportingMaterials:
          data?.supportingMaterials?.map((material, index) => ({
            id: index,
            name: material.name,
            url: material.url,
          })) || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateLesson = async e => {
    e.preventDefault();
    try {
      const data = {
        courseId: courseId,
        moduleId: moduleId,
        type: lesson.type,
        bannerUrl: lesson.bannerUrl,
        name: lesson.name,
        content: lesson.content,
        videoUrl: lesson.videoUrl,
        duration: lesson.duration,
        supportingMaterials: lesson.supportingMaterials.map(material => ({
          name: material.name,
          url: material.url,
        })),
      };

      await createLesson(data);
      onSave();
      handleClose();
      toast.success('Lesson created successfully');
    } catch (error) {
      console.log(error.response);
      console.error('Error creating lesson', error.message);
      toast.error(`Failed to create lesson: ${error?.response?.data?.error?.message}`);
    }
  };

  const handleUpdateLesson = async e => {
    e.preventDefault();
    try {
      const data = {
        name: lesson.name,
        bannerUrl: lesson.bannerUrl,
        content: lesson.content,
        videoUrl: lesson.videoUrl,
        duration: lesson.duration,
        supportingMaterials: lesson.supportingMaterials.map(material => ({
          name: material.name,
          url: material.url,
        })),
      };

      await updateLesson(lessonId, data);
      onSave();
      handleClose();
      toast.success('Lesson updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update lesson', {
        description: error.response.data.message,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="education__course__new-lesson">
      <h3>{lessonId ? 'Edit Lesson' : 'New Lesson'}</h3>
      <button onClick={handleClose} className="education__course__new-lesson__close">
        <TimesIcon />
      </button>

      <Callout
        title="Important"
        description="Lessons are organized in ascending order by date of creation. Create them following the logical sequence of the content."
      />
      <form className="education__course__new-lesson__form" onSubmit={lessonId ? handleUpdateLesson : handleCreateLesson} noValidate>
        <div className="education__course__new-lesson__form__row">
          <TextField
            label="Name"
            value={lesson.name}
            onChange={e => setLesson({ ...lesson, name: e.target.value })}
            className="education__course__new-lesson__form__row__name"
          />
          <div className="education__course__new-lesson__form__row__duration">
            <TextField
              type="number"
              label="Duration (seconds)"
              value={lesson.duration}
              onChange={e => setLesson({ ...lesson, duration: e.target.value })}
            />
            <span>{formatDuration(lesson.duration)}</span>
          </div>
        </div>

        <TextField label="Video URL" value={lesson.videoUrl} onChange={e => setLesson({ ...lesson, videoUrl: e.target.value })} />
        <TextField label="Banner URL" value={lesson.bannerUrl} onChange={e => setLesson({ ...lesson, bannerUrl: e.target.value })} />

        <ContentEditor content={lesson.content} setContent={value => setLesson({ ...lesson, content: value })} />
        <div className="education__course__new-lesson__form__supporting-materials">
          <div className="education__course__new-lesson__form__supporting-materials__header">
            <h2>Supporting Materials</h2>
            <Button
              variant="contained"
              onClick={() =>
                setLesson({
                  ...lesson,
                  supportingMaterials: [...lesson.supportingMaterials, { id: lesson.supportingMaterials.length, name: '', url: '' }],
                })
              }
            >
              <PlusIcon />
            </Button>
          </div>
          <div className="education__course__new-lesson__form__supporting-materials__list">
            {formatedMaterial.map((material, index) => (
              <div key={index} className="education__course__new-lesson__form__supporting-materials__item">
                <div className="education__course__new-lesson__form__supporting-materials__item__left">
                  <TextField
                    label="Name"
                    value={material.name}
                    onChange={e =>
                      setLesson({
                        ...lesson,
                        supportingMaterials: lesson.supportingMaterials.map((m, i) => (i === index ? { ...m, name: e.target.value } : m)),
                      })
                    }
                  />
                  <TextField
                    label="URL"
                    value={material.url}
                    onChange={e =>
                      setLesson({
                        ...lesson,
                        supportingMaterials: lesson.supportingMaterials.map((m, i) => (i === index ? { ...m, url: e.target.value } : m)),
                      })
                    }
                  />
                </div>
                <Button
                  variant="contained"
                  onClick={() => setLesson({ ...lesson, supportingMaterials: lesson.supportingMaterials.filter((_, i) => i !== index) })}
                >
                  <TrashCanIcon />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="education__course__new-lesson__form__buttons">
          <Button variant="contained" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" className="education__course__new-lesson__form__buttons__submit">
            {lessonId ? 'Update Lesson' : 'Create Lesson'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default NewLessonModal;
